import { useEffect, useState, RefObject } from "react";

export interface UsePieChartSizingOptions {
  containerRef: RefObject<HTMLDivElement>;
  shouldShowLegend: boolean;
  orientation: "horizontal" | "vertical";
}

export interface UsePieChartSizingReturn {
  innerRadius: number;
  outerRadius: number;
}

/**
 * Simple hook to calculate responsive pie chart radius based on container size
 * Works with Recharts ResponsiveContainer which handles the main responsive behavior
 * This hook just calculates the radius values that need to update on resize
 */
export const usePieChartSizing = ({
  containerRef,
  shouldShowLegend,
  orientation,
}: UsePieChartSizingOptions): UsePieChartSizingReturn => {
  const [radius, setRadius] = useState({ innerRadius: 70, outerRadius: 140 });

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const calculateRadius = () => {
      const rect = element.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;

      // Account for legend space
      const availableWidth = shouldShowLegend && orientation === "vertical"
        ? rect.width * 0.7
        : rect.width;
      const availableHeight = shouldShowLegend && orientation === "horizontal"
        ? rect.height * 0.75
        : rect.height;

      const minDimension = Math.min(availableWidth, availableHeight);
      const calculatedOuterRadius = Math.max(50, minDimension * 0.4);
      const calculatedInnerRadius = calculatedOuterRadius * 0.5;

      setRadius({
        innerRadius: calculatedInnerRadius,
        outerRadius: calculatedOuterRadius,
      });
    };

    // Calculate initially
    calculateRadius();

    // Use ResizeObserver to recalculate on container resize
    const resizeObserver = new ResizeObserver(calculateRadius);
    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, [containerRef, shouldShowLegend, orientation]);

  return radius;
};

